/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import type {
	ConditionObject,
	IConditionForm,
	LabelConditionItem,
} from 'interfaces';
import {generateUUID} from 'utils';

/**
 * Initializes the default form data for the application.
 * Populates fields with either existing data from the database
 * or default values when no data is provided.
 *
 * @param {Partial<IConditionForm>} [dataFromDb] - Optional data retrieved from the database.
 * @returns {IConditionForm} - The initialized form data object.
 */
export const defaultFormDataCondition = (
	dataFromDb?: Partial<IConditionForm>,
	isGroup?: boolean,
): IConditionForm => {
	const initializeField = <T>(
		field: ConditionObject<T> | undefined,
		defaultValue: T,
	): ConditionObject<T> => ({
		value: field?.value ?? defaultValue,
		notChecked: field?.notChecked ?? false,
		active: field?.active ?? false,
		uuid: field?.uuid ?? generateUUID(),
	});

	const initializeObject = <T>(field: T | undefined, defaultObject: T): T => ({
		...defaultObject,
		...field,
	});

	const initArrayField = <T>(
		field: ConditionObject<T>[] | undefined,
		defaultValue: T,
	): ConditionObject<T>[] => {
		if (!field || field.length === 0) {
			return [initializeField(undefined, defaultValue)];
		}
		return field.map(f => initializeField(f, defaultValue));
	};

	const initArrayComplexField = <TInput, TOutput>(
		field: TInput[] | undefined | null,
		initializer: (item: TInput) => TOutput,
		defaultEmpty: () => TOutput,
	): TOutput[] => {
		const safeArray = Array.isArray(field) ? field : [];
		if (safeArray.length === 0) {
			return [defaultEmpty()];
		}
		return safeArray.map(initializer);
	};

	return {
		id: `condi-${isGroup ? 'AND' : 'OR'}-${generateUUID()}`,
		isGroup: dataFromDb?.isGroup ?? isGroup ?? false,
		deviceTac: initArrayField(dataFromDb?.deviceTac, ''),
		deviceModel: initArrayField(dataFromDb?.deviceModel, ''),
		tac: initArrayField(dataFromDb?.tac, ''),
		lac: initArrayField(dataFromDb?.lac, ''),
		sac: initializeField(dataFromDb?.sac, ''),
		area: initArrayField(dataFromDb?.area, ''),
		eutraCellId: initArrayField(dataFromDb?.eutraCellId, ''),
		tai: initArrayComplexField(
			dataFromDb?.tai,
			item => ({
				tac: initializeField(item?.tac, ''),
				plmnId: initializeObject(item?.plmnId, {
					mcc: initializeField(item?.plmnId?.mcc, ''),
					mnc: initializeField(item?.plmnId?.mnc, ''),
				}),
			}),
			() => ({
				tac: initializeField(undefined, ''),
				plmnId: {
					mcc: initializeField(undefined, ''),
					mnc: initializeField(undefined, ''),
				},
			}),
		),
		ncgi: {
			nrCellId: initializeField(dataFromDb?.ncgi?.nrCellId, ''),
			plmnId: initializeObject(dataFromDb?.ncgi?.plmnId, {
				mcc: initializeField(dataFromDb?.ncgi?.plmnId?.mcc, ''),
				mnc: initializeField(dataFromDb?.ncgi?.plmnId?.mnc, ''),
			}),
			nid: initializeField(dataFromDb?.ncgi?.nid, ''),
		},
		sai: initArrayComplexField(
			dataFromDb?.sai,
			item => ({
				lac: initializeField(item?.lac, ''),
				sac: initializeField(item?.sac, ''),
				plmnId: initializeObject(item?.plmnId, {
					mcc: initializeField(item?.plmnId?.mcc, ''),
					mnc: initializeField(item?.plmnId?.mnc, ''),
				}),
			}),
			() => ({
				lac: initializeField(undefined, ''),
				sac: initializeField(undefined, ''),
				plmnId: {
					mcc: initializeField(undefined, ''),
					mnc: initializeField(undefined, ''),
				},
			}),
		),
		ecgi: initArrayComplexField(
			dataFromDb?.ecgi,
			item => ({
				eutraCellId: initializeField(item?.eutraCellId, ''),
				plmnId: initializeObject(item?.plmnId, {
					mcc: initializeField(item?.plmnId?.mcc, ''),
					mnc: initializeField(item?.plmnId?.mnc, ''),
				}),
			}),
			() => ({
				eutraCellId: initializeField(undefined, ''),
				plmnId: {
					mcc: initializeField(undefined, ''),
					mnc: initializeField(undefined, ''),
				},
			}),
		),
		chargingCharacteristics: initArrayField(
			dataFromDb?.chargingCharacteristics,
			'',
		),
		online: initializeField(dataFromDb?.online, false),
		offline: initializeField(dataFromDb?.offline, false),
		dnn: initArrayField(dataFromDb?.dnn, ''),
		accessType: initArrayField(dataFromDb?.accessType, ''),
		ratType: initArrayField(dataFromDb?.ratType, ''),
		signalingPath: initializeField(dataFromDb?.signalingPath, false),
		afAppId: initArrayField(dataFromDb?.afAppId, ''),
		codec: initArrayField(dataFromDb?.codec, ''),
		flowUsage: initArrayField(dataFromDb?.flowUsage, ''),
		fStatus: initArrayField(dataFromDb?.fStatus, ''),
		rrBw: initArrayField(dataFromDb?.rrBw, ''),
		rsBw: initArrayField(dataFromDb?.rsBw, ''),
		servUrn: initArrayField(dataFromDb?.servUrn, ''),
		medType: initArrayField(dataFromDb?.medType, ''),
		marBwUl: initArrayField(dataFromDb?.marBwUl, ''),
		marBwDl: initArrayField(dataFromDb?.marBwDl, ''),
		mirBwUl: initArrayField(dataFromDb?.mirBwUl, ''),
		mirBwDl: initArrayField(dataFromDb?.mirBwDl, ''),
		servingNetwork: initArrayComplexField(
			dataFromDb?.servingNetwork,
			serviNet => ({
				mcc: initializeField(serviNet?.mcc, ''),
				mnc: initializeField(serviNet?.mnc, ''),
			}),
			() => ({
				mcc: initializeField(undefined, ''),
				mnc: initializeField(undefined, ''),
			}),
		),
		planType: initializeField(dataFromDb?.planType, ''),
		usageMonitoring: initializeField(dataFromDb?.usageMonitoring, ''),
		finalAction: initializeField(dataFromDb?.finalAction, false),
		homeZone: initializeField(dataFromDb?.homeZone, false),
		tenant: initializeField(dataFromDb?.tenant, ''),
		category: initArrayField(dataFromDb?.category, ''),
		pei: initArrayField(dataFromDb?.pei, ''),
		time: initArrayField(dataFromDb?.time, ''),
		roaming: initArrayField(dataFromDb?.roaming, ''),
	};
};

/**
 * List of predefined conditions for the application.
 * Includes both single string labels and categories with their associated items.
 *
 * @constant
 * @type {Array<string | Record<string, string[]>>}
 */
export const labelConditions: LabelConditionItem[] = [
	{
		category: 'Dnn',
		scope: ['App', 'Gw'],
	},
	{
		category: 'Access Type',
		scope: ['App', 'Gw'],
	},
	{
		category: 'RAT Type',
		scope: ['App', 'Gw'],
	},
	{
		category: 'Signaling path sessions',
		visibleSwitch: false,
		items: [{category: 'Signaling Path'}],
		scope: ['App'],
	},
	{
		category: 'Non signaling path sessions',
		visibleSwitch: false,
		items: [
			{category: 'AF Application Id'},
			{category: 'Codec'},
			{category: 'Media Type'},
			{category: 'Flow Usage', visibleSwitch: false, allowedInGroupOr: false},
			{category: 'Flow Status'},
			{category: 'Max Requested Bandwidth Upload'},
			{category: 'Max Requested Bandwidth Download'},
			{category: 'Min requested Bandwidth Upload'},
			{category: 'Min Requested Bandwidth Download'},
			{category: 'RR Bw'},
			{category: 'RS Bw'},
		],
		scope: ['App'],
	},
	{
		category: 'Emergency Traffic',
		visibleSwitch: false,
		items: [{category: 'Service URN'}],
		scope: ['App'],
	},
	{
		category: 'Charging',
		visibleSwitch: false,
		items: [
			{category: 'Online', visibleSwitch: false},
			{category: 'Offline', visibleSwitch: false},
			{category: 'Charging Characteristics'},
		],
		scope: ['App', 'Gw'],
	},
	{
		category: 'Roaming',
		visibleSwitch: false,
		items: [
			{category: 'Roaming'},
			{
				category: 'Serving Network',
				visibleSwitch: false,
				items: [{category: 'MNC / MCC'}],
				scope: ['Gw'],
			},
		],
		scope: ['Gw'],
	},
	{
		category: 'Data',
		visibleSwitch: false,
		items: [
			{category: 'Plan Type', visibleSwitch: false, allowedInGroupOr: false},
			{
				category: 'Usage Monitoring',
				visibleSwitch: false,
				allowedInGroupOr: false,
			},
			{category: 'Final Action', visibleSwitch: false, allowedInGroupOr: false},
			{category: 'Home Zone', visibleSwitch: false},
			{category: 'Category'},
		],
		scope: ['Gw'],
		isVolTE: false,
	},
	{
		category: 'Time',
		visibleSwitch: false,
		items: [{category: 'Time', visibleSwitch: false, allowedInGroupOr: false}],
		scope: ['Gw'],
		allowedInGroupOr: false,
	},
	{
		category: 'User Equipment Info',
		visibleSwitch: false,
		items: [{category: 'PEI'}],
		scope: ['App', 'Gw'],
	},
	{
		category: 'Location',
		visibleSwitch: false,
		items: [
			{category: 'TAC'},
			{category: 'EUTRA Cell Id'},
			{category: 'Area'},
			{category: 'LAC'},
			{
				category: 'TAI',
				items: [
					{category: 'TAC', visibleSwitch: false},
					{
						category: 'PLMN_ID',
						visibleSwitch: false,
						items: [
							{category: 'MNC', visibleSwitch: false},
							{category: 'MCC', visibleSwitch: false},
						],
						scope: ['App', 'Gw'],
					},
				],
				scope: ['App', 'Gw'],
			},
			{
				category: 'ECGI',
				items: [
					{category: 'EUTRA Cell Id', visibleSwitch: false},
					{
						category: 'PLMN_ID',
						visibleSwitch: false,
						items: [
							{category: 'MNC', visibleSwitch: false},
							{category: 'MCC', visibleSwitch: false},
						],
						scope: ['App', 'Gw'],
					},
				],
				scope: ['App', 'Gw'],
			},
			{
				category: 'SAI',
				items: [
					{category: 'LAC', visibleSwitch: false},
					{category: 'SAC', visibleSwitch: false},
					{
						category: 'PLMN_ID',
						visibleSwitch: false,
						items: [
							{category: 'MNC', visibleSwitch: false},
							{category: 'MCC', visibleSwitch: false},
						],
						scope: ['App', 'Gw'],
					},
				],
				scope: ['App', 'Gw'],
			},
		],
		scope: ['App', 'Gw'],
	},
	{
		category: 'Device related',
		visibleSwitch: false,
		items: [{category: 'Device Model'}, {category: 'Device TAC'}],
		scope: ['App', 'Gw'],
	},
];

export type TransformedItem = {
	category: string;
	items: TransformedItem[];
	scope?: ('App' | 'Gw')[];
	visibleSwitch: boolean;
	isVolTE: boolean;
};

export const transformLabelConditions = (
	conditions: LabelConditionItem[],
	typeForm: 'App' | 'Gw',
	isCategoryVolTE: boolean,
	isGroupOr = false,
): TransformedItem[] => {
	function filterItems(
		items: LabelConditionItem[],
		parentScope: ('App' | 'Gw')[],
	): TransformedItem[] {
		return items
			.map(item => {
				if (
					(isCategoryVolTE && item.isVolTE === false) ||
					(!isCategoryVolTE && item.isVolTE === true)
				) {
					return null;
				}

				const effectiveScope = item.scope ?? parentScope;

				if (!effectiveScope.includes(typeForm)) return null;

				if (isGroupOr && item.allowedInGroupOr === false) {
					return null;
				}

				const currentVisibleSwitch = item.visibleSwitch ?? true;

				const filteredSubItems = item.items
					? filterItems(item.items, effectiveScope)
					: [];

				return {
					category: item.category,
					items: filteredSubItems,
					scope: effectiveScope,
					visibleSwitch: currentVisibleSwitch,
				};
			})
			.filter(Boolean) as TransformedItem[];
	}
	return filterItems(conditions, [typeForm]);
};

export const labelToFieldMap: Record<string, keyof IConditionForm | string[]> =
	{
		Dnn: 'dnn',
		'Access Type': 'accessType',
		'RAT Type': 'ratType',
		'Charging Online': 'online',
		'Charging Offline': 'offline',
		'Charging Charging Characteristics': 'chargingCharacteristics',
		'Roaming Serving Network MNC': ['servingNetwork', 'mnc'],
		'Roaming Serving Network MCC': ['servingNetwork', 'mcc'],
		'Roaming Roaming': 'roaming',
		'Signaling path sessions Signaling Path': 'signalingPath',
		'Non signaling path sessions AF Application Id': 'afAppId',
		'Non signaling path sessions Codec': 'codec',
		'Non signaling path sessions Media Type': 'medType',
		'Non signaling path sessions Flow Usage': 'flowUsage',
		'Non signaling path sessions Flow Status': 'fStatus',
		'Non signaling path sessions Max Requested Bandwidth Upload': 'marBwUl',
		'Non signaling path sessions Max Requested Bandwidth Download': 'marBwDl',
		'Non signaling path sessions Min requested Bandwidth Upload': 'mirBwUl',
		'Non signaling path sessions Min Requested Bandwidth Download': 'mirBwDl',
		'Non signaling path sessions RR Bw': 'rrBw',
		'Non signaling path sessions RS Bw': 'rsBw',
		'Emergency Traffic Service URN': 'servUrn',
		'Location TAI TAC': ['tai', 'tac'],
		'Location TAI': ['tai'],
		'Location TAI PLMN_ID MCC': ['tai', 'plmnId', 'mcc'],
		'Location TAI PLMN_ID MNC': ['tai', 'plmnId', 'mnc'],
		'Location SAI': ['sai'],
		'Location SAI PLMN_ID MNC': ['sai', 'plmnId', 'mnc'],
		'Location SAI PLMN_ID MCC': ['sai', 'plmnId', 'mcc'],
		'Location SAI LAC': ['sai', 'lac'],
		'Location SAI SAC': ['sai', 'sac'],
		'Location ECGI': ['ecgi'],
		'Location ECGI EUTRA Cell Id': ['ecgi', 'eutraCellId'],
		'Location ECGI PLMN_ID MNC': ['ecgi', 'plmnId', 'mnc'],
		'Location ECGI PLMN_ID MCC': ['ecgi', 'plmnId', 'mcc'],
		'Data Plan Type': 'planType',
		'Data Usage Monitoring': 'usageMonitoring',
		'Data Final Action': 'finalAction',
		'Data Home Zone': 'homeZone',
		'Data Category': 'category',
		'Time Time': 'time',
		'User Equipment Info PEI': 'pei',
		'Location TAC': 'tac',
		'Location EUTRA Cell Id': 'eutraCellId',
		'Location Area': 'area',
		'Location LAC': 'lac',
		'Device related Device Model': 'deviceModel',
		'Device related Device TAC': 'deviceTac',
	};

export function getNestedField<T>(obj: any, path: string[]): T | undefined {
	return path.reduce((acc, key) => acc?.[key], obj);
}

export function setNestedField<T>(obj: any, path: string[], value: T): any {
	const [key, ...rest] = path;

	if (!key) return value;

	return {
		...obj,
		[key]:
			rest.length > 0 ? setNestedField(obj?.[key] ?? {}, rest, value) : value,
	};
}

export function resolveFieldKey(
	...labels: string[]
): string | string[] | undefined {
	const key = labels.join(' ');
	return labelToFieldMap[key];
}
